/**
 * frontend/src/pages/valuation/_OwnerFiguresReview.tsx
 *
 * VAL-PATH-TYPED-FINANCIALS (P2c) — on the signer's Review step: the years the
 * owner typed, and the signer's acceptance with a reason. The acceptance is the
 * existing draft save (`tuning.owner_figures_accepted`); the server stamps the
 * figures, who and when, so the screen reads the draft back after saving.
 * An acceptance whose figures no longer match the typed figures is stale.
 */
import { useCallback, useEffect, useState } from 'react'

import { fetchFinancials } from '@/api/valuationFinancials'
import { fetchValuationDraft, saveValuationDraft } from '@/api/valuationB3'
import type { OwnerFiguresAccepted } from '@/types/valuationDraft'
import { formatDateTimeCentral } from '@/utils/date'
import { formatApiError } from '@/utils/httpErrors'
import { isOwnerEntered } from './_FinancialsTyping'
import { CLIENT_TEXT } from './_typedFinancialsText'

type Figures = Record<string, number | null>

function typedFigures(provenance: Record<string, unknown>): Figures {
  const out: Figures = {}
  for (const [k, p] of Object.entries(provenance)) if (isOwnerEntered(p)) out[k] = p.value
  return out
}

function sameFigures(a: Figures, b: Figures | undefined): boolean {
  if (!b) return false
  const keys = Object.keys(a)
  return keys.length === Object.keys(b).length && keys.every(k => b[k] === a[k])
}

export function OwnerFiguresReview({ engagementId }: { engagementId: string }) {
  const [figures, setFigures] = useState<Figures | null>(null)
  const [accepted, setAccepted] = useState<OwnerFiguresAccepted | null>(null)
  const [acceptedByName, setAcceptedByName] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const readAccepted = useCallback(async () => {
    const d = await fetchValuationDraft(engagementId)
    const t = (d?.tuning ?? null) as { owner_figures_accepted?: OwnerFiguresAccepted | null } | null
    setAccepted(t?.owner_figures_accepted ?? null)
    setAcceptedByName(d?.accepted_by_name ?? null)
  }, [engagementId])

  useEffect(() => {
    let live = true
    Promise.all([fetchFinancials(engagementId), readAccepted()])
      .then(([v]) => { if (live) setFigures(typedFigures(v.provenance ?? {})) })
      .catch(() => { /* the Review step stands without this box */ })
    return () => { live = false }
  }, [engagementId, readAccepted])

  if (!figures || Object.keys(figures).length === 0) return null

  const years = [...new Set(Object.keys(figures).map(k => Number(k.split('.')[0])))].sort((a, b) => b - a)
  const current = !!accepted && sameFigures(figures, accepted.figures)
  const stale = !!accepted && !current

  async function accept() {
    if (!reason.trim()) { setError(CLIENT_TEXT.REVIEW_NEED_REASON); return }
    setBusy(true)
    setError(null)
    try {
      await saveValuationDraft(engagementId, { tuning: { owner_figures_accepted: { reason: reason.trim() } } })
      await readAccepted()
      setReason('')
    } catch (err: unknown) {
      setError(formatApiError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section aria-labelledby="owner-figures-heading" className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm">
      <h2 id="owner-figures-heading" className="font-semibold text-gray-900">{CLIENT_TEXT.REVIEW_HEADING}</h2>
      <p className="text-gray-800">{years.map(y => `FY${y}`).join(', ')}</p>
      <p className="text-gray-700">{CLIENT_TEXT.REVIEW_INTRO}</p>
      {accepted && current && (
        <p className="text-gray-800">
          {/* WALKFIX-C F1: the person's name and Central time, not the email and UTC. */}
          {CLIENT_TEXT.REVIEW_ACCEPTED(acceptedByName || accepted.by || '', formatDateTimeCentral(accepted.at), accepted.reason)}
        </p>
      )}
      {stale && <p className="text-amber-900">{CLIENT_TEXT.REVIEW_STALE}</p>}
      {!current && (
        <div className="space-y-2">
          <label className="block text-gray-800">
            {CLIENT_TEXT.REVIEW_REASON_LABEL}
            <textarea className="mt-1 block w-full rounded border border-gray-300 p-2 text-sm"
              value={reason} onChange={e => setReason(e.target.value)} />
          </label>
          <button type="button" disabled={busy} onClick={() => void accept()}
            className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-50">
            {CLIENT_TEXT.REVIEW_ACCEPT}
          </button>
        </div>
      )}
      {error && <p role="alert" className="text-red-800">{error}</p>}
    </section>
  )
}
